import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import RouteIcon from "../Icons/RouteIcon";

/**
 * POI = Google Places result from nearbySearch
 * We need getDetails to retrieve formatted_address (and sometimes geometry)
 */
const getPoiDetails = (placesService, placeId) =>
  new Promise((resolve, reject) => {
    if (!placesService || !placeId)
      return reject(new Error("Missing service/placeId"));

    placesService.getDetails(
      {
        placeId,
        // fields: ["name", "formatted_address", "geometry", "vicinity"],
      },
      (details, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          details
        ) {
          resolve(details);
        } else {
          reject(new Error(`Places getDetails failed: ${status}`));
        }
      },
    );
  });

/**
 * Props:
 * - placeId: string
 * - fallbackName: string (from nearbySearch)
 * - fallbackVicinity: string (from nearbySearch)
 * - markerLat / markerLng: number (fallback position)
 * - placesService: google.maps.places.PlacesService (from your ref)
 * - onPickPoint: ({ latLng, label }) => void
 */
const PoiInfoWindow = ({
  placeId,
  fallbackName = "",
  fallbackVicinity = "",
  markerLat,
  markerLng,
  placesService,
  onSetPointA,
  onSetPointB,
}) => {
  const [poi, setPoi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(false);

        // fetch details (address)
        const details = await getPoiDetails(placesService, placeId);
        if (!cancelled) setPoi(details);
      } catch (e) {
        // fallback: still show something
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [placeId, placesService]);

  // LatLng for routing button
  const latLng =
    poi?.geometry?.location ||
    (Number.isFinite(markerLat) && Number.isFinite(markerLng)
      ? new window.google.maps.LatLng(markerLat, markerLng)
      : null);

  return (
    <div className="alignC" style={{ width: 260, height: 380 }}>
      {loading ? (
        <div className="alignC flex-column gap-3 bold">
          <Spinner variant="orange" animation="grow" />
        </div>
      ) : error || !poi ? (
        <div
          className="text-center bold f12"
          style={{
            color: "#b91c1c",
          }}
        >
          Erreur lors du chargement. <br /> Veuillez rafraîchir la page.
        </div>
      ) : (
        <div className="d-flex flex-column justify-content-between h100 w100">
          <img
            src={
              poi?.photos?.[0]?.getUrl({ maxWidth: 260, maxHeight: 180 }) ??
              "./assets/imgNotFound.webp"
            }
            alt={poi.name}
            style={{
              width: "100%",
              height: 180,
              objectFit: "cover",
              borderRadius: 10,
              marginBottom: 10,
              background: "#f3f4f6",
              display: "block",
            }}
            loading="lazy"
          />
          <hr />
          <div className="d-flex flex-column gap-2">
            <div className="bold f14">{poi.name || fallbackName || ""}</div>
            <div className="f12 alignH">
              <img
                src="./icons/icon_location_grey.svg"
                width={16}
                height={16}
                alt="Localisation"
              />
              {poi?.formatted_address ||
                poi?.vicinity ||
                fallbackVicinity ||
                ""}
            </div>
          </div>
          <hr />
          <div className="alignH gap-1">
            <a
              href={poi?.url}
              target="_blank"
              rel="noreferrer"
              className="pink1 radius10 flex-1"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 40,
                textDecoration: "none",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              Voir sur Maps
            </a>
            <button
              style={{
                alignItems: "center",
                justifyContent: "center",
                height: 40,
                textDecoration: "none",
                fontWeight: 600,
                fontSize: 14,
              }}
              className="pink1 radius10 px-3 w30"
              onClick={() =>
                onSetPointA?.({
                  latLng: new window.google.maps.LatLng(markerLat, markerLng),
                  label: poi?.name || fallbackName || "",
                })
              }
            >
              <RouteIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PoiInfoWindow;
